import React, { useState, useEffect } from 'react';
import {
  FAST_LANE_NUMERIC_FIELDS,
  SignupFlowSyncCredentialCondition,
  SignupFlowQualificationSignalType,
  ResumeQualificationSignalField,
  LinkedInQualificationSignalField,
  StringOperator,
  NumberOperator,
} from '@scale/shared-types';
import {
  useResumeMetadataValuesQuery,
  convertToOpensearchField,
  useFetchFastLaneListsQuery,
} from '../../api';
import {
  Flex,
  Text,
  Box,
  Select,
  TextField,
  IconButton,
  Icon,
  Autocomplete,
} from '@scale/scaleui-radix';
import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import _ from 'lodash';

const isNumericField = (
  field: ResumeQualificationSignalField | LinkedInQualificationSignalField,
): boolean => {
  return FAST_LANE_NUMERIC_FIELDS.includes(field);
};

export const fieldNameToDisplayNameMapping = {
  // Resume fields
  [ResumeQualificationSignalField.School]: 'School',
  [ResumeQualificationSignalField.Degree]: 'Degree',
  [ResumeQualificationSignalField.FieldOfStudy]: 'Field of Study',
  [ResumeQualificationSignalField.CompanyName]: 'Company Name',
  [ResumeQualificationSignalField.JobTitle]: 'Job Title',
  [ResumeQualificationSignalField.JobCountry]: 'Job Country',
  [ResumeQualificationSignalField.YearsOfExperience]: 'Years of Experience',
  [ResumeQualificationSignalField.LanguageSpoken]: 'Language Spoken',
  [ResumeQualificationSignalField.Skills]: 'Job Skills',
  [ResumeQualificationSignalField.StandardizedTestName]: 'Standardized Test Name',
  [ResumeQualificationSignalField.PublicationSource]: 'Publication Source',
  [ResumeQualificationSignalField.AccreditationName]: 'Accreditation Name',
  [ResumeQualificationSignalField.AccreditationOrganization]: 'Accreditation Organization',

  // LinkedIn fields
  [LinkedInQualificationSignalField.NumConnections]: 'Number of Connections',
  [LinkedInQualificationSignalField.NumFollowers]: 'Number of Followers',
  [LinkedInQualificationSignalField.CountryCode]: 'Country Code',
  [LinkedInQualificationSignalField.School]: 'School',
  [LinkedInQualificationSignalField.Degree]: 'Degree',
  [LinkedInQualificationSignalField.FieldOfStudy]: 'Field of Study',
  [LinkedInQualificationSignalField.Company]: 'Company',
  [LinkedInQualificationSignalField.JobTitle]: 'Job Title',
};

const SignupFlowFastLaneQualificationCondition: React.FC<{
  condition: SignupFlowSyncCredentialCondition;
  conditionIndex: number;
  groupIndex: number;
  updateCondition: (
    groupIndex: number,
    conditionIndex: number,
    updatedCondition: Partial<SignupFlowSyncCredentialCondition>,
  ) => void;
  removeCondition: (groupIndex: number, conditionIndex: number) => void;
  totalConditions: number;
}> = ({
  condition,
  conditionIndex,
  groupIndex,
  updateCondition,
  removeCondition,
  totalConditions,
}) => {
  const [multiValueInput, setMultiValueInput] = useState<string>('');
  const [opensearchSearchValue, setOpensearchSearchValue] = useState<string>('');

  useEffect(() => {
    if (Array.isArray(condition.value)) {
      setMultiValueInput(condition.value.join(', '));
    }
  }, [condition.value]);

  const { data, isFetching: isFetchingOpensearchValues } = useResumeMetadataValuesQuery(
    condition.field,
    opensearchSearchValue,
  );
  const opensearchValues = data?.values;

  const { data: fastLaneListsResponse, isFetching: isFetchingFastLaneLists } =
    useFetchFastLaneListsQuery();
  const filteredListOptions = fastLaneListsResponse?.fastLaneLists.filter(list =>
    list.fields.includes(condition.field),
  );

  const operatorOptions = FAST_LANE_NUMERIC_FIELDS.includes(condition.field)
    ? [
        { name: 'Minimum', id: NumberOperator.Min },
        { name: 'Maximum', id: NumberOperator.Max },
        { name: 'Equals', id: NumberOperator.Equals },
      ]
    : [
        { name: 'Equals', id: StringOperator.Equals },
        { name: 'One Of', id: StringOperator.OneOf },
        // Show list operator if lists exist for this field, or while loading to prevent selection reset
        ...(!_.isEmpty(filteredListOptions) || isFetchingFastLaneLists
          ? [{ name: 'One Of (List)', id: StringOperator.InList }]
          : []),
      ];

  return (
    <Flex
      key={`condition-${groupIndex}-${conditionIndex}`}
      direction="row"
      align="center"
      gap="3"
      mb="3"
    >
      <Box minWidth="5%" className="text-right justify-center">
        <Text size="1" weight="medium" className="align-right min-w-16">
          {conditionIndex === 0 ? '' : 'AND'}
        </Text>
      </Box>

      {/* Signal Type */}
      <Box minWidth="20%">
        <Select.Root
          value={condition.signalType}
          onValueChange={value => {
            const signalType = value as SignupFlowQualificationSignalType;
            // When signal type changes, reset the field and operator to a valid one for that type
            const field =
              signalType === SignupFlowQualificationSignalType.Resume
                ? ResumeQualificationSignalField.School
                : LinkedInQualificationSignalField.School;

            updateCondition(groupIndex, conditionIndex, {
              signalType,
              field,
              // Reset value and operator when type changes
              value: typeof condition.value === 'number' ? 0 : '',
              operator: StringOperator.Equals,
            });
          }}
        >
          <Select.Trigger placeholder="Signal type" variant="surface" className="w-full" />
          <Select.Content>
            <Select.Item value={SignupFlowQualificationSignalType.Resume}>Resume</Select.Item>
            <Select.Item value={SignupFlowQualificationSignalType.LinkedIn}>LinkedIn</Select.Item>
          </Select.Content>
        </Select.Root>
      </Box>

      {/* Field */}
      <Box minWidth="20%">
        <Select.Root
          value={condition.field}
          onValueChange={value => {
            const field = value as any;
            const isNumeric = [
              LinkedInQualificationSignalField.NumConnections,
              LinkedInQualificationSignalField.NumFollowers,
              ResumeQualificationSignalField.YearsOfExperience,
            ].includes(field);

            updateCondition(groupIndex, conditionIndex, {
              field,
              // Update operator based on field type
              operator: isNumeric ? NumberOperator.Min : StringOperator.Equals,
              // Reset value when field changes
              value: isNumeric ? 0 : '',
            });
          }}
        >
          <Select.Trigger placeholder="Field" variant="surface" className="w-full" />
          <Select.Content>
            {condition.signalType === SignupFlowQualificationSignalType.Resume
              ? Object.values(ResumeQualificationSignalField).map(field => (
                  <Select.Item key={field} value={field}>
                    {
                      fieldNameToDisplayNameMapping[
                        field as keyof typeof fieldNameToDisplayNameMapping
                      ]
                    }
                  </Select.Item>
                ))
              : Object.values(LinkedInQualificationSignalField).map(field => (
                  <Select.Item key={field} value={field}>
                    {
                      fieldNameToDisplayNameMapping[
                        field as keyof typeof fieldNameToDisplayNameMapping
                      ]
                    }
                  </Select.Item>
                ))}
          </Select.Content>
        </Select.Root>
      </Box>

      {/* Operator */}
      <Box minWidth="20%">
        <Select.Root
          value={condition.operator}
          onValueChange={value => {
            updateCondition(groupIndex, conditionIndex, {
              operator: value as any,
              // Reset value when operator changes
              value: isNumericField(condition.field)
                ? 0
                : value === StringOperator.OneOf || value === StringOperator.InList
                ? []
                : '',
            });
          }}
        >
          <Select.Trigger placeholder="Operator" variant="surface" className="w-full" />
          <Select.Content>
            {operatorOptions.map(option => (
              <Select.Item key={option.id} value={option.id}>
                {option.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Box>

      {/* Value input - changes based on field type and operator */}
      <Box flexGrow="1">
        {(() => {
          // Handle numeric fields
          if (isNumericField(condition.field)) {
            return (
              <TextField.Root
                type="number"
                placeholder="Enter value..."
                value={condition.value.toString()}
                onChange={e => {
                  updateCondition(groupIndex, conditionIndex, {
                    value: parseInt(e.target.value) || 0,
                  });
                }}
              />
            );
          }

          // Handle predefined list operator
          else if (condition.operator === StringOperator.InList) {
            return (
              <Autocomplete.Root
                multiple
                value={condition.value as string[]}
                onValueChange={(newValues: string[]) => {
                  updateCondition(groupIndex, conditionIndex, {
                    value: newValues,
                  });
                }}
              >
                <Autocomplete.Content>
                  <Autocomplete.TextField placeholder="Search options...">
                    <Autocomplete.Values />
                  </Autocomplete.TextField>
                  <Autocomplete.AsyncItems isFetching={isFetchingFastLaneLists}>
                    {filteredListOptions?.map(option => (
                      <Autocomplete.Item key={option.key} value={option.key}>
                        {`${option.displayName} (${option.numValues})`}
                      </Autocomplete.Item>
                    ))}
                  </Autocomplete.AsyncItems>
                </Autocomplete.Content>
              </Autocomplete.Root>
            );
          }

          // Handle Opensearch fields
          else if (convertToOpensearchField(condition.field)) {
            return (
              <Autocomplete.Root
                multiple={condition.operator === StringOperator.OneOf}
                // $FIXME: condition.value might be a number
                value={condition.value as string | string[]}
                onValueChange={(newValues: string | string[]) => {
                  updateCondition(groupIndex, conditionIndex, {
                    value: newValues,
                  });
                }}
                onSearchChange={setOpensearchSearchValue}
                search={opensearchSearchValue}
              >
                <Autocomplete.Content>
                  <Autocomplete.TextField
                    className={`${
                      Array.isArray(condition.value) && condition.value.length === 0
                        ? '[&>input]:pl-0 w-full'
                        : 'w-full'
                    }`}
                    placeholder="Search options..."
                  >
                    <Autocomplete.Values />
                  </Autocomplete.TextField>

                  <Autocomplete.AsyncItems isFetching={isFetchingOpensearchValues}>
                    {opensearchValues?.map(option => (
                      <Autocomplete.Item key={option.value} value={option.value}>
                        {`${option.value} (${option.count})`}
                      </Autocomplete.Item>
                    ))}
                  </Autocomplete.AsyncItems>
                </Autocomplete.Content>
              </Autocomplete.Root>
            );
          }

          // Handle oneOf operator
          else if (condition.operator === StringOperator.OneOf) {
            return (
              <TextField.Root
                value={multiValueInput}
                onBlur={() => {
                  const values = multiValueInput
                    .split(',')
                    .map(v => v.trim())
                    .filter(v => v.length > 0);

                  updateCondition(groupIndex, conditionIndex, {
                    value: values,
                  });
                }}
                onChange={e => {
                  setMultiValueInput(e.target.value);
                }}
                placeholder="Enter comma-separated values..."
              />
            );
          }

          // Handle string equals
          else {
            return (
              <TextField.Root
                value={condition.value as string}
                onChange={e => {
                  updateCondition(groupIndex, conditionIndex, {
                    value: e.target.value,
                  });
                }}
                placeholder="Enter value..."
              />
            );
          }
        })()}
      </Box>

      {/* Remove button */}
      {totalConditions > 1 && (
        <IconButton
          size="1"
          color="neutral"
          variant="ghost"
          onClick={() => removeCondition(groupIndex, conditionIndex)}
          ml="0"
        >
          <Icon label="Remove condition" icon={faMinus} color="neutral" />
        </IconButton>
      )}
    </Flex>
  );
};

export default SignupFlowFastLaneQualificationCondition;
